// PORTED FROM apps/web — see apps/web-employer/PORTING.md (or web-admin/) for follow-ups

import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslations } from "next-intl";
import { Receipt, RefreshCw } from "lucide-react";
import {
  Button,
  Card,
  ConfirmDialog,
  Dialog,
  EmptyState,
  SkeletonCard,
  useToast,
} from "@chotca/ui";
import type { InterviewCreditPurchase } from "@chotca/sdk";
import { EmployerAnalyticsEvent } from "@chotca/config";
import { ErrorState } from "@/components/error-state";
import { logAnalyticsEvent } from "@/lib/analytics";
import { useMe } from "@chotca/api-client";
import { useOrg } from "@chotca/api-client";
import {
  useCancelCreditPurchase,
  useCreateCreditPurchase,
  useCreditPurchases,
  usePublicPricing,
  useSyncCreditPurchase,
} from "@chotca/api-client";

type Pack = {
  code: string;
  count: number;
  amountVnd: number;
  unit: number;
  original: number;
};

// Fallback only — the buy modal reads live pricing from /public/credit-packs
// (admin-editable) and falls back to these constants while that query loads or
// if it fails. Keep in sync with packages/db/seed.ts.
const FALLBACK_PACKS: Pack[] = [
  {
    code: "PACK_10",
    count: 10,
    amountVnd: 250_000,
    unit: 25_000,
    original: 500_000,
  },
  {
    code: "PACK_25",
    count: 25,
    amountVnd: 500_000,
    unit: 20_000,
    original: 1_000_000,
  },
  {
    code: "PACK_125",
    count: 125,
    amountVnd: 2_000_000,
    unit: 16_000,
    original: 4_000_000,
  },
  {
    code: "PACK_400",
    count: 400,
    amountVnd: 5_000_000,
    unit: 12_500,
    original: 10_000_000,
  },
];

type CheckoutHandoff = { url: string; formFields: Record<string, string> };

const STATUS_LABEL: Record<InterviewCreditPurchase["status"], string> = {
  PENDING: "Chờ thanh toán",
  PAID: "Đã thanh toán",
  EXPIRED: "Hết hạn",
  FAILED: "Đã huỷ",
};

const STATUS_TONE: Record<InterviewCreditPurchase["status"], string> = {
  PENDING: "bg-amber-100 text-amber-900",
  PAID: "bg-lime/30 text-ink",
  EXPIRED: "bg-mist text-ash",
  FAILED: "bg-danger/10 text-danger",
};

function fmtVnd(v: number | string) {
  return Number(v).toLocaleString("vi-VN") + "đ";
}

function fmtDateTime(iso: string) {
  return new Date(iso).toLocaleString("vi-VN");
}

// Revenue event. `creditedNow` is the backend's exactly-once credit signal, so
// firing here records each paid purchase once (a re-sync returns creditedNow
// false). No PII — only the purchase id, count and amount.
function trackCreditCompleted(p: InterviewCreditPurchase) {
  logAnalyticsEvent(EmployerAnalyticsEvent.creditPurchaseCompleted, {
    purchase_id: p.id,
    credit_count: p.creditCount,
    amount_vnd: Number(p.amountVnd),
  });
}

type CreditModalsContextValue = {
  openBuy: () => void;
  openHistory: () => void;
};

const CreditModalsContext = createContext<CreditModalsContextValue | null>(
  null,
);

export function useCreditModals() {
  const ctx = useContext(CreditModalsContext);
  if (!ctx) {
    throw new Error("useCreditModals must be used within CreditModalsProvider");
  }
  return ctx;
}

export function CreditModalsProvider({ children }: { children: ReactNode }) {
  const [buyOpen, setBuyOpen] = useState(false);
  const [historyOpen, setHistoryOpen] = useState(false);
  const [returnFlag, setReturnFlag] = useState<string | null>(null);
  const nav = useNavigate();
  const [search] = useSearchParams();

  // Sepay redirects back to /employer?credits=ok|cancel|error. Snapshot the
  // flag into state, auto-open the history modal, and strip the query so a
  // refresh doesn't re-fire reconciliation.
  const urlFlag = search.get("credits");
  useEffect(() => {
    if (!urlFlag) return;
    setReturnFlag(urlFlag);
    setHistoryOpen(true);
    const url = new URL(window.location.href);
    url.searchParams.delete("credits");
    nav(url.pathname + (url.search || ""), { replace: true });
  }, [urlFlag, nav]);

  const value = useMemo<CreditModalsContextValue>(
    () => ({
      openBuy: () => setBuyOpen(true),
      openHistory: () => setHistoryOpen(true),
    }),
    [],
  );

  return (
    <CreditModalsContext.Provider value={value}>
      {children}
      <BuyCreditsModal
        open={buyOpen}
        onClose={() => setBuyOpen(false)}
        onShowHistory={() => {
          setBuyOpen(false);
          setHistoryOpen(true);
        }}
      />
      <CreditHistoryModal
        open={historyOpen}
        onClose={() => {
          setHistoryOpen(false);
          setReturnFlag(null);
        }}
        onBuy={() => {
          setHistoryOpen(false);
          setBuyOpen(true);
        }}
        returnFlag={returnFlag}
      />
    </CreditModalsContext.Provider>
  );
}

// ───────────────────────────────────────────────────────────── Buy modal

function BuyCreditsModal({
  open,
  onClose,
  onShowHistory,
}: {
  open: boolean;
  onClose: () => void;
  onShowHistory: () => void;
}) {
  const t = useTranslations("creditPurchases");
  const me = useMe();
  const orgId = me.data?.memberships[0]?.orgId;
  const org = useOrg(orgId);
  const create = useCreateCreditPurchase(orgId ?? "");
  const { toast } = useToast();
  const pricing = usePublicPricing();

  // Live, admin-editable pricing from the server; fall back to constants while
  // the query loads or if it errors so the modal is never empty.
  const packs = useMemo<Pack[]>(() => {
    const rows = pricing.data?.packs;
    if (!rows || rows.length === 0) return FALLBACK_PACKS;
    return [...rows]
      .sort((a, b) => a.creditCount - b.creditCount)
      .map((p) => ({
        code: p.packCode,
        count: p.creditCount,
        amountVnd: Number(p.amountVnd),
        unit: Number(p.unitPriceVnd),
        original: p.originalPriceVnd ? Number(p.originalPriceVnd) : 0,
      }));
  }, [pricing.data]);

  const [confirmPack, setConfirmPack] = useState<Pack | null>(null);
  const [submittingPack, setSubmittingPack] = useState<string | null>(null);
  const [handoff, setHandoff] = useState<CheckoutHandoff | null>(null);

  // Reset transient state when modal closes
  useEffect(() => {
    if (!open) {
      setConfirmPack(null);
      setSubmittingPack(null);
      setHandoff(null);
    }
  }, [open]);

  async function startPayment(pack: Pack) {
    if (!orgId) return;
    setSubmittingPack(pack.code);
    try {
      const origin =
        typeof window !== "undefined" ? window.location.origin : "";
      const res = await create.mutateAsync({
        packCode: pack.code,
        returnUrl: `${origin}/dashboard?credits=ok`,
        cancelUrl: `${origin}/dashboard?credits=cancel`,
        errorUrl: `${origin}/dashboard?credits=error`,
      });
      setConfirmPack(null);
      setHandoff(res.checkout);
    } catch {
      toast({ tone: "error", title: t("purchaseFailed") });
      setSubmittingPack(null);
    }
  }

  return (
    <>
      <Dialog
        open={open && !confirmPack && !handoff}
        onClose={() => {
          if (submittingPack) return;
          onClose();
        }}
        title={t("title")}
        description={t("subtitle")}
        size="lg"
      >
        <div className="space-y-4 sm:space-y-5">
          <Card className="bg-lime p-4 sm:p-5">
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="text-label uppercase text-ink/70">
                  {t("balanceLabel")}
                </p>
                <p className="mt-0.5 text-title-lg font-black text-ink sm:text-display-lg">
                  {(org.data?.inviteCreditsAvailable ?? 0).toLocaleString(
                    "vi-VN",
                  )}{" "}
                  <span className="text-title-md font-semibold text-ink/70">
                    {t("balanceUnit")}
                  </span>
                </p>
              </div>
              <button
                type="button"
                onClick={onShowHistory}
                className="shrink-0 rounded-full bg-paper px-3.5 py-2 text-body-md font-semibold text-ink shadow-sm transition-colors hover:bg-paper/80"
              >
                {t("purchaseHistory")} →
              </button>
            </div>
          </Card>

          <div className="grid grid-cols-2 gap-3">
            {packs.map((p) => (
              <Card key={p.code} bordered className="p-3 sm:p-4">
                <p className="text-title-lg font-extrabold text-ink sm:text-display-md">
                  {p.count}{" "}
                  <span className="text-body-sm font-semibold text-ash sm:text-body-md">
                    {t("balanceUnit")}
                  </span>
                </p>
                {p.original > p.amountVnd ? (
                  <p className="mt-1 flex items-center gap-1.5">
                    <span className="text-body-sm text-ash line-through">
                      {fmtVnd(p.original)}
                    </span>
                    <span className="rounded-full bg-ink px-1.5 py-0.5 text-[10px] font-bold text-lime">
                      -{Math.round((1 - p.amountVnd / p.original) * 100)}%
                    </span>
                  </p>
                ) : null}
                <p className="mt-0.5 text-body-md font-bold text-ink">
                  {fmtVnd(p.amountVnd)}
                </p>
                <p className="text-body-sm text-ash">
                  {fmtVnd(p.unit)} / {t("balanceUnit")}
                </p>
                <Button
                  type="button"
                  fullWidth
                  size="sm"
                  onClick={() => setConfirmPack(p)}
                  className="mt-3"
                >
                  {t("buyCta")}
                </Button>
              </Card>
            ))}
          </div>
        </div>
      </Dialog>

      <Dialog
        open={!!confirmPack && !handoff}
        onClose={() => {
          if (submittingPack) return;
          setConfirmPack(null);
        }}
        title="Xác nhận thanh toán"
        description="Bạn sẽ được chuyển sang trang thanh toán Sepay để hoàn tất giao dịch."
        footer={
          <div className="flex w-full flex-col-reverse gap-2 sm:w-auto sm:flex-row">
            <Button
              type="button"
              variant="ghost"
              fullWidth
              onClick={() => setConfirmPack(null)}
              disabled={!!submittingPack}
            >
              Huỷ
            </Button>
            <Button
              type="button"
              fullWidth
              loading={!!submittingPack}
              onClick={() => confirmPack && startPayment(confirmPack)}
            >
              Tiếp tục thanh toán
            </Button>
          </div>
        }
      >
        {confirmPack ? (
          <div className="space-y-3 text-body-md text-ink">
            <div className="flex items-center justify-between">
              <span className="text-ash">Gói</span>
              <span className="font-semibold">
                {confirmPack.count} {t("balanceUnit")}
              </span>
            </div>
            <div className="flex items-center justify-between">
              <span className="text-ash">Đơn giá</span>
              <span>
                {fmtVnd(confirmPack.unit)} / {t("balanceUnit")}
              </span>
            </div>
            <div className="flex items-center justify-between border-t border-smoke pt-3">
              <span className="text-ash">Tổng thanh toán</span>
              <span className="text-title-lg font-extrabold">
                {fmtVnd(confirmPack.amountVnd)}
              </span>
            </div>
          </div>
        ) : null}
      </Dialog>

      {handoff ? <SepayCheckoutForm handoff={handoff} /> : null}
    </>
  );
}

// ─────────────────────────────────────────────────────────── History modal

function CreditHistoryModal({
  open,
  onClose,
  onBuy,
  returnFlag,
}: {
  open: boolean;
  onClose: () => void;
  onBuy: () => void;
  returnFlag: string | null;
}) {
  const t = useTranslations("creditPurchases");
  const me = useMe();
  const orgId = me.data?.memberships[0]?.orgId;
  const purchases = useCreditPurchases(orgId ?? "", open);
  const sync = useSyncCreditPurchase(orgId ?? "");
  const cancel = useCancelCreditPurchase(orgId ?? "");
  const { toast } = useToast();

  const [pendingActionId, setPendingActionId] = useState<string | null>(null);
  const [confirmCancelId, setConfirmCancelId] = useState<string | null>(null);

  // Auto-reconcile on Sepay return: when modal opens with returnFlag, find
  // the latest pending purchase and sync it server-side.
  const reconciled = useRef(false);
  useEffect(() => {
    if (!open) {
      reconciled.current = false;
      return;
    }
    if (reconciled.current || !returnFlag || !orgId) return;
    if (
      returnFlag !== "ok" &&
      returnFlag !== "cancel" &&
      returnFlag !== "error"
    )
      return;

    // All branches need the list to locate the pending row this return refers
    // to — wait until it has loaded before reconciling.
    const list = purchases.data;
    if (!list) return;
    const recentPending = list.find((p) => p.status === "PENDING");

    // Sepay never marks an abandoned/cancelled checkout on its own order, so
    // /sync can't detect it — the cancel/error signal lives only in this return
    // URL. Mark the pending row FAILED here instead of leaving it stuck on
    // "Chờ thanh toán" until the 30-min expiry cron sweeps it.
    if (returnFlag === "cancel" || returnFlag === "error") {
      reconciled.current = true;
      const cancelTitle =
        returnFlag === "cancel"
          ? "Bạn đã huỷ thanh toán"
          : "Thanh toán thất bại, vui lòng thử lại";
      if (!recentPending) {
        toast({
          tone: returnFlag === "cancel" ? "default" : "error",
          title: cancelTitle,
        });
        return;
      }
      cancel
        .mutateAsync(recentPending.id)
        .then(() => {
          toast({
            tone: returnFlag === "cancel" ? "default" : "error",
            title: cancelTitle,
          });
        })
        .catch(() => {
          toast({
            tone: returnFlag === "cancel" ? "default" : "error",
            title: cancelTitle,
          });
        });
      return;
    }

    // returnFlag === "ok"
    if (!recentPending) {
      reconciled.current = true;
      return;
    }
    reconciled.current = true;
    sync
      .mutateAsync(recentPending.id)
      .then((r) => {
        if (r.creditedNow) {
          trackCreditCompleted(r.purchase);
          toast({
            tone: "success",
            title: `Đã cộng ${r.purchase.creditCount} lượt phỏng vấn`,
          });
        } else if (r.purchase.status === "PAID") {
          toast({ tone: "success", title: "Thanh toán đã được xác nhận" });
        } else if (
          r.providerStatus === "PENDING" ||
          r.providerStatus === "UNKNOWN"
        ) {
          toast({
            tone: "warning",
            title: "Chưa nhận được thanh toán",
            description:
              "Hệ thống sẽ tự kiểm tra lại sau vài phút. Bạn có thể bấm 'Kiểm tra lại'.",
          });
        } else {
          toast({ tone: "error", title: `Thanh toán: ${r.providerStatus}` });
        }
      })
      .catch(() => {
        toast({
          tone: "error",
          title: "Không kiểm tra được trạng thái, thử lại sau",
        });
      });
  }, [open, returnFlag, orgId, purchases.data?.length]);

  async function handleSync(purchaseId: string) {
    setPendingActionId(purchaseId);
    try {
      const r = await sync.mutateAsync(purchaseId);
      if (r.creditedNow) {
        trackCreditCompleted(r.purchase);
        toast({
          tone: "success",
          title: `Đã cộng ${r.purchase.creditCount} lượt phỏng vấn`,
        });
      } else if (r.purchase.status === "PAID") {
        toast({ tone: "success", title: "Đã xác nhận thanh toán" });
      } else if (
        r.providerStatus === "PENDING" ||
        r.providerStatus === "UNKNOWN"
      ) {
        toast({
          tone: "default",
          title: "Chưa thấy thanh toán. Vui lòng đợi vài phút.",
        });
      } else {
        toast({ tone: "warning", title: `Trạng thái: ${r.providerStatus}` });
      }
    } catch {
      toast({ tone: "error", title: "Không kiểm tra được, thử lại sau" });
    } finally {
      setPendingActionId(null);
    }
  }

  const performCancel = useCallback(
    async (purchaseId: string) => {
      setPendingActionId(purchaseId);
      try {
        await cancel.mutateAsync(purchaseId);
        toast({ tone: "success", title: t("cancelSuccess") });
        setConfirmCancelId(null);
      } catch {
        toast({ tone: "error", title: t("cancelError") });
      } finally {
        setPendingActionId(null);
      }
    },
    [cancel, t, toast],
  );

  function resumeCheckout(p: InterviewCreditPurchase) {
    if (!p.providerCheckoutUrl) return;
    window.location.href = p.providerCheckoutUrl;
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={onClose}
        title={t("purchaseHistory")}
        size="lg"
        footer={
          <Button type="button" fullWidth className="sm:w-auto" onClick={onBuy}>
            {t("buyCta")}
          </Button>
        }
      >
        {purchases.isLoading ? (
          <div className="space-y-2">
            <SkeletonCard />
            <SkeletonCard />
          </div>
        ) : purchases.isError ? (
          <ErrorState onRetry={() => purchases.refetch()} />
        ) : purchases.data && purchases.data.length > 0 ? (
          <div className="divide-y divide-smoke">
            {purchases.data.map((p) => (
              <div key={p.id} className="py-4 first:pt-0 last:pb-0">
                <div className="flex items-start justify-between gap-2.5">
                  <p className="min-w-0 text-body-md font-semibold text-ink">
                    {p.creditCount} {t("balanceUnit")} — {fmtVnd(p.amountVnd)}
                  </p>
                  <span
                    className={`shrink-0 rounded-full px-2.5 py-1 text-body-sm font-semibold ${STATUS_TONE[p.status]}`}
                  >
                    {STATUS_LABEL[p.status]}
                  </span>
                </div>
                <p className="mt-1 text-body-sm text-ash">
                  {fmtDateTime(p.createdAt)} · #{p.providerOrderId}
                </p>
                {p.status === "PENDING" ? (
                  <div className="mt-3 flex items-stretch gap-2">
                    <button
                      type="button"
                      aria-label="Làm mới trạng thái"
                      title="Làm mới"
                      disabled={
                        pendingActionId !== null && pendingActionId !== p.id
                      }
                      onClick={() => handleSync(p.id)}
                      className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-mist text-ink transition-colors hover:bg-smoke disabled:opacity-50"
                    >
                      <RefreshCw
                        size={16}
                        strokeWidth={2.25}
                        className={
                          pendingActionId === p.id && sync.isPending
                            ? "animate-spin"
                            : ""
                        }
                      />
                    </button>
                    {p.providerCheckoutUrl ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="primary"
                        className="flex-1"
                        onClick={() => resumeCheckout(p)}
                      >
                        Tiếp tục thanh toán
                      </Button>
                    ) : null}
                    <Button
                      type="button"
                      size="sm"
                      variant="ghost"
                      className="flex-1"
                      loading={pendingActionId === p.id && cancel.isPending}
                      disabled={
                        pendingActionId !== null && pendingActionId !== p.id
                      }
                      onClick={() => setConfirmCancelId(p.id)}
                    >
                      {t("cancelCta")}
                    </Button>
                  </div>
                ) : null}
              </div>
            ))}
          </div>
        ) : (
          <EmptyState
            icon={<Receipt className="h-7 w-7" />}
            title={t("purchaseEmpty")}
          />
        )}
      </Dialog>

      <ConfirmDialog
        open={!!confirmCancelId}
        onClose={() => setConfirmCancelId(null)}
        onConfirm={() => {
          if (confirmCancelId) void performCancel(confirmCancelId);
        }}
        title={t("cancelTitle")}
        description={t("cancelDesc")}
        confirmLabel={t("cancelCta")}
        cancelLabel={t("cancelKeep")}
        destructive
        loading={
          confirmCancelId !== null &&
          pendingActionId === confirmCancelId &&
          cancel.isPending
        }
      />
    </>
  );
}

function SepayCheckoutForm({ handoff }: { handoff: CheckoutHandoff }) {
  const formRef = useRef<HTMLFormElement>(null);

  useEffect(() => {
    formRef.current?.submit();
  }, []);

  return (
    <div className="fixed inset-0 z-60 flex items-center justify-center bg-ink/60 backdrop-blur-sm">
      <div className="rounded-[24px] bg-paper p-8 text-center shadow-xl">
        <p className="text-title-md font-bold text-ink">
          Đang chuyển sang Sepay…
        </p>
        <p className="mt-2 text-body-sm text-ash">
          Trình duyệt sẽ tự mở trang thanh toán trong giây lát.
        </p>
      </div>
      <form ref={formRef} method="POST" action={handoff.url} className="hidden">
        {Object.entries(handoff.formFields).map(([name, value]) => (
          <input key={name} type="hidden" name={name} value={value} />
        ))}
      </form>
    </div>
  );
}
